import { createReducer, on } from '@ngrx/store';
import { Partner } from '@hub-services/partner';
import { User } from '@hub-services/user.service';
import * as SessionActions from './session.actions';
import { SessionPayload } from './session.actions';
import * as PartnerActions from '@hub-store/selected-partner/selected-partner.actions';
import { PartnerType } from '../../admin/admin-partners/partner-type.enum';

export const featureKey = 'session';

export interface HubSessionState {
  data: {
    partner_id: number | null;
    partner_type: PartnerType | null;
    user_id: string | null;
  } | null,
  included: {
    partners: Partial<Partner>[];
    users: User[];
  } | null,
  partner_id: number | null;
  partner_type: PartnerType | null;
  user_id: string | null;
}

export const initialState: HubSessionState = {
  data: null,
  included: null,
  partner_id: null,
  partner_type: null,
  user_id: null,
};

function persistSessionState(state: HubSessionState): HubSessionState {
  if (state.data?.user_id) {
    window.sessionStorage.setItem('sessionData', JSON.stringify(state));
  }

  return state;
}

function normalizeSessionPayload(
  state: HubSessionState,
  payload: SessionPayload,
): HubSessionState | null {
  if (!payload) {
    return state;
  }

  const incomingUserId = payload.user_id ?? payload.data?.user_id ?? null;
  if (incomingUserId && state.user_id != null && incomingUserId !== state.user_id) {
    return null;
  }

  const partnerId = payload.partner_id ?? payload.data?.partner_id ?? state.partner_id ?? null;
  const partnerType = payload.partner_type ?? payload.data?.partner_type ?? state.partner_type ?? null;
  const userId = incomingUserId ?? state.user_id ?? null;

  return {
    ...state,
    ...payload,
    partner_id: partnerId,
    partner_type: partnerType,
    user_id: userId,
    included: payload.included ?? state.included,
    data: {
      ...state.data,
      ...payload.data,
      partner_id: partnerId,
      partner_type: partnerType,
      user_id: userId,
    }
  };
}

export const reducer = createReducer(
  initialState,
  on(PartnerActions.setPartner, (state, action) => persistSessionState({
    ...state,
    partner_id: action.partner?.id || state.partner_id || null,
    partner_type: action.partner
      ? (action.partner?.carrier_code
        ? PartnerType.AIRLINE
        : (action.partner?.type ? action.partner?.type.toLowerCase() as PartnerType : state.partner_type))
      : state.partner_type,
    data: {
      ...state.data,
      partner_id: action.partner?.id || state.partner_id || null,
      partner_type: action.partner
        ? (action.partner?.carrier_code
          ? PartnerType.AIRLINE
          : (action.partner?.type ? action.partner?.type.toLowerCase() as PartnerType : state.partner_type))
        : state.partner_type,
      // state.data is nullable, so the spread above leaves user_id optional; set it explicitly
      // (mirrors the partner_id/partner_type handling) so `data` still satisfies HubSessionState.
      user_id: state.data?.user_id ?? state.user_id ?? null,
      }
  })),

  on(SessionActions.setSessionData, (state, action) => {
    const normalizedState = normalizeSessionPayload(state, action.data);
    return persistSessionState(normalizedState ?? state);
  }),

  on(SessionActions.updateSessionUserInStore, (state, action) => {
    // Assumes a session user is already loaded (included.users[0]) — this action only fires
    // from post-hydration profile-update flows. No-op guard mirrors the reducer-guard pattern
    // in selected-partner.reducer's addCategoryToStore: nothing to update yet.
    if (!state.included?.users?.length) return state;

    return persistSessionState({
      ...state,
      included: {
        ...state.included,
        users: [{
          ...state.included.users[0],
          ...action.user
        }]
      }
    });
  }),

);
